import get from "lodash.get";
export interface QueryParams {
	page: number;
	pageSize: number;
	sort?: Dryp.SortQuery;
	filters?: { id: string; value: any }[];
	globalFilter?: string;
}

export interface TableQueryParams<T> extends QueryParams {
	data: T[];
}

interface TableQueryResult<T> {
	queryData: T[];
	pagination: {
		page: number;
		pageSize: number;
		total: number;
	};
	query: {
		sort?: { id: keyof T; desc: boolean }[];
		filters?: { id: keyof T; value: any }[];
		globalFilter?: string;
	};
}

const removeCustomFilters = (filters?: { id: string; value: any }[]) => {
	if (!filters) {
		return filters;
	}
	return filters.filter(({ id }) => !id.startsWith("__") && !id.startsWith("q:"));
};

const getSearchFilters = (filters?: { id: string; value: any }[]) => {
	if (!filters) {
		return filters;
	}
	return filters.filter(({ id }) => id.startsWith("q:"));
};

export const mockServerTableQuery = <T extends Record<string, any>>(
	params: TableQueryParams<T>,
): TableQueryResult<T> => {
	let page = params.page;
	const searchFilters = getSearchFilters(params.filters);
	let filtered = applyGlobalFilter(params.data, searchFilters);
	const columnFilters = removeCustomFilters(params.filters);
	filtered = applyColumnFilters(filtered, columnFilters);
	filtered = applySorting(filtered);

	const totalPages = Math.ceil(filtered.length / params.pageSize);
	if (page >= totalPages) page = 0;

	const paginated = applyPagination(filtered, page, params.pageSize);

	return {
		queryData: paginated,
		pagination: {
			page,
			pageSize: params.pageSize,
			total: filtered.length,
		},
		query: {
			filters: params.filters,
			globalFilter: params.globalFilter,
		},
	};
};

const applyGlobalFilter = <T extends Record<string, any>>(
	data: T[],
	searchFilters?: { id: string; value: any }[],
): T[] => {
	if (!searchFilters || searchFilters.length === 0) return data;
	return data.filter(item =>
		searchFilters.every(filter => {
			const field = filter.id.slice(2);
			const itemValue = String(get(item, field) ?? "").toLowerCase();
			const searchValue = String(filter.value ?? "").toLowerCase();

			return itemValue.includes(searchValue);
		}),
	);
};

const applyColumnFilters = <T extends Record<string, any>>(data: T[], filters?: { id: string; value: any }[]): T[] => {
	if (!filters || filters.length === 0) return data;

	return filters.reduce((filteredData, filter) => {
		const values = Array.isArray(filter.value) ? filter.value : [filter.value];

		return filteredData.filter(item => {
			const raw = get(item, filter.id);

			if (typeof raw !== "number") return false;
			if (!values || values.length === 0) return true;

			return values.some(range => {
				if (range.endsWith("+")) {
					const min = parseFloat(range);
					return raw > min;
				} else if (range.includes("-")) {
					const [min, max] = range.split("-").map(Number);
					return raw >= min && raw < max;
				}
				return false;
			});
		});
	}, data);
};

const applySorting = <T>(data: T[], sort?: { id: string; desc: boolean }[]): T[] => {
	if (!sort || sort.length === 0) return data;

	const { id, desc } = sort[0];
	return [...data].sort((a, b) => {
		const aVal = getNestedValue(a, id);
		const bVal = getNestedValue(b, id);

		if (aVal < bVal) return desc ? 1 : -1;
		if (aVal > bVal) return desc ? -1 : 1;
		return 0;
	});
};

const applyPagination = <T>(data: T[], page: number, pageSize: number): T[] => {
	const start = page * pageSize;
	const end = start + pageSize;
	return data.slice(start, end);
};

const getNestedValue = (obj: any, path: string): any => path.split(".").reduce((acc, key) => acc?.[key], obj);
